/**
 * photo.js — la photo porte sa date, son heure et son adresse
 *
 * Covage l'exige : une photo de compte rendu doit être horodatée et située,
 * en clair, sur l'image elle-même. Une métadonnée EXIF ne suffit pas — elle
 * ne se voit pas, et la plupart des outils qui recompressent une image la
 * perdent en chemin. Le bandeau est donc dessiné dans les pixels, avant
 * l'envoi : il survit au PDF, à la pièce jointe et à la capture d'écran.
 *
 * L'adresse est celle où le technicien se trouve, pas celle de la commande.
 * C'est ce que Covage demande, et c'est aussi plus honnête : sur un immeuble
 * de quarante logements, une photo prise au point de mutualisation n'est pas
 * prise à l'adresse du client. On la lit en géocodant à l'envers la position
 * du téléphone ; si le GPS se tait, on retombe sur l'adresse de la commande,
 * et le bandeau dit laquelle des deux il montre.
 *
 * Une position est gardée deux minutes : dix photos d'affilée sur le même
 * chantier ne doivent pas déclencher dix relevés.
 */

import { adresseDuPoint } from './api';

const FRAICHEUR_MS = 2 * 60 * 1000;

let _adresseCommande = '';
let _lieu = null;        // { libelle, source, at }
let _enCours = null;     // la promesse en vol, partagée par les appels groupés

const dormir = ms => new Promise(r => setTimeout(r, ms));

/** La position du téléphone, sans jamais faire échouer l'appelant. */
function position() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => resolve(p),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: FRAICHEUR_MS },
    );
  });
}

/**
 * Où nous sommes, en une ligne lisible.
 *
 * `repli` est l'adresse de la commande, utilisée quand le GPS ne répond pas.
 * On ne bloque jamais plus de huit secondes : une photo qui n'est pas prise
 * parce que le GPS cherchait le ciel serait un bien mauvais échange.
 */
async function lieu(repli) {
  const frais = _lieu && Date.now() - _lieu.at < FRAICHEUR_MS;
  if (frais) return _lieu;
  if (_enCours) return _enCours;

  _enCours = (async () => {
    const p = await position();
    if (!p) {
      _lieu = { libelle: repli || '', source: repli ? 'commande' : 'aucune', at: Date.now() };
      return _lieu;
    }
    const { latitude, longitude } = p.coords;
    let libelle = '';
    try {
      const r = await adresseDuPoint(latitude, longitude);
      libelle = (r && r.libelle) || '';
    } catch { /* hors ligne : les coordonnées feront foi */ }
    _lieu = {
      libelle: libelle || repli || '',
      source: libelle ? 'position' : (repli ? 'commande' : 'coordonnees'),
      lat: latitude, lon: longitude,
      at: Date.now(),
    };
    return _lieu;
  })().finally(() => { _enCours = null; });

  return _enCours;
}

/**
 * L'adresse de la commande, posée une fois par dossier.
 *
 * Elle sert de repli quand le GPS ne répond pas — sous-sol, parking,
 * autorisation coupée. Posée ici plutôt que passée à chaque photo : une
 * page de compte rendu compte trente blocs de photos, et trente props de
 * plus ne diraient rien que ce seul appel ne dise.
 */
export function poserAdresseCommande(a) { _adresseCommande = String(a || '').trim(); }

/** Oublie la position retenue — au changement de dossier. */
export function oublierLieu() { _lieu = null; }

/**
 * Réduire sans horodater.
 *
 * Pour ce qui n'est pas une photo de terrain : la capture d'écran d'un test
 * VIA, par exemple. Y incruster une adresse serait un faux — l'image ne
 * vient pas du chantier.
 */
export function compresserSeulement(dataUrl, maxPx = 1200, qualite = 0.75) {
  return preparerPhoto(dataUrl, { maxPx, qualite, sansBandeau: true });
}

/**
 * Ce que la photo sait d'elle-meme : l'heure de la prise de vue, et le lieu.
 *
 * Le bandeau portait l'heure du depot. Tant que le technicien photographie
 * et ajoute dans la foulee, c'est la meme chose a une minute pres. Mais
 * celui qui photographie le chantier le matin et remplit son compte rendu
 * le soir obtenait l'heure du soir, et la position du soir — son domicile.
 * Devant Covage, ce serait pire que pas d'horodatage du tout.
 *
 * On lit donc DateTimeOriginal, que tout appareil photo ecrit. Sans EXIF —
 * une capture d'ecran, une image recompressee par une messagerie — on
 * retombe sur l'heure du depot, qui reste vraie.
 *
 * Et la position, pour la meme raison : l'appareil photo du telephone ecrit
 * les coordonnees dans l'EXIF, le navigateur rend le fichier d'origine quand
 * le technicien choisit une photo dans sa galerie. Quand elles sont la, elles
 * valent mieux que la position du depot — elles disent ou la photo a ete
 * prise, pas ou le technicien se trouvait en remplissant son formulaire.
 *
 * Deux cas la privent de coordonnees, et le repli reste donc necessaire : une
 * photo prise par l'appareil integre a l'application, qui n'ecrit pas d'EXIF,
 * et un telephone dont la localisation est coupee pour l'appareil photo.
 *
 * Rend `{ date, lat, lon }`, chaque champ valant null s'il est absent.
 */
function metaPhoto(dataUrl) {
  const RIEN = { date: null, lat: null, lon: null };
  try {
    const virgule = dataUrl.indexOf(',');
    if (virgule < 0) return RIEN;
    // L'EXIF tient dans les premiers kilo-octets : inutile de decoder une
    // photo de trois megaoctets pour y lire une date.
    const brut = atob(dataUrl.slice(virgule + 1, virgule + 1 + 262144));
    const o = new DataView(Uint8Array.from(brut, c => c.charCodeAt(0)).buffer);
    if (o.getUint16(0) !== 0xFFD8) return RIEN;   // pas un JPEG

    // Parcourir les segments jusqu'a APP1.
    let p = 2, tiff = -1;
    while (p + 4 < o.byteLength) {
      if (o.getUint8(p) !== 0xFF) break;
      const marqueur = o.getUint8(p + 1);
      const taille = o.getUint16(p + 2);
      if (marqueur === 0xE1 && o.getUint32(p + 4) === 0x45786966) { tiff = p + 10; break; }
      if (marqueur === 0xDA) break;               // debut de l'image
      p += 2 + taille;
    }
    if (tiff < 0 || tiff + 8 > o.byteLength) return RIEN;

    const petitBoutiste = o.getUint16(tiff) === 0x4949;
    const u16 = i => o.getUint16(i, petitBoutiste);
    const u32 = i => o.getUint32(i, petitBoutiste);
    if (u16(tiff + 2) !== 0x002A) return RIEN;

    // Trouver un champ dans une table d'entrees de 12 octets.
    const chercher = (ifd, tag) => {
      if (ifd + 2 > o.byteLength) return 0;
      const n = u16(ifd);
      for (let i = 0; i < n; i++) {
        const e = ifd + 2 + i * 12;
        if (e + 12 > o.byteLength) return 0;
        if (u16(e) === tag) return e;
      }
      return 0;
    };
    const texte = (e, max) => {
      const debut = tiff + u32(e + 8);
      let t = '';
      for (let i = 0; i < max && debut + i < o.byteLength; i++) {
        const c = o.getUint8(debut + i);
        if (!c) break;
        t += String.fromCharCode(c);
      }
      return t;
    };

    const ifd0 = tiff + u32(tiff + 4);

    // ── L'heure ──
    let date = null;
    let e = 0;
    const pointeur = chercher(ifd0, 0x8769);         // vers le sous-repertoire EXIF
    if (pointeur) {
      const exif = tiff + u32(pointeur + 8);
      e = chercher(exif, 0x9003) || chercher(exif, 0x9004);  // prise / numerisation
    }
    if (!e) e = chercher(ifd0, 0x0132);              // a defaut, la date du fichier
    if (e) {
      // Format EXIF : 'AAAA:MM:JJ hh:mm:ss', toujours en heure locale.
      const m = texte(e, 19).match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
      if (m) {
        const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
        // Une date absurde (appareil mal regle, horloge a zero) ne vaut pas
        // mieux que pas de date : on la laisse tomber en silence.
        const ecart = isNaN(d) ? null : Date.now() - d.getTime();
        if (ecart !== null && ecart > -3600000 && ecart < 30 * 86400000) date = d;
      }
    }

    // ── Le lieu ──
    //
    // Les coordonnees GPS tiennent en trois RATIONAL — degres, minutes,
    // secondes — chacun deux entiers de quatre octets. Le signe est porte a
    // part, par une lettre : S et W comptent en negatif.
    let lat = null, lon = null;
    const gpsPtr = chercher(ifd0, 0x8825);
    if (gpsPtr) {
      const gps = tiff + u32(gpsPtr + 8);
      const dms = (tag) => {
        const c = chercher(gps, tag);
        if (!c || u16(c + 2) !== 5 || u32(c + 4) !== 3) return null;   // RATIONAL x3
        const base = tiff + u32(c + 8);
        if (base + 24 > o.byteLength) return null;
        let v = 0;
        for (let i = 0; i < 3; i++) {
          const num = u32(base + i * 8), den = u32(base + i * 8 + 4);
          if (!den) return null;
          v += (num / den) / Math.pow(60, i);
        }
        return v;
      };
      const ref = (tag) => {
        const c = chercher(gps, tag);
        if (!c) return '';
        // Une lettre tient dans les quatre octets de l'entree : elle y est
        // ecrite en place, et non au bout d'un decalage.
        return String.fromCharCode(o.getUint8(c + 8)).toUpperCase();
      };
      const la = dms(0x0002), lo = dms(0x0004);
      if (la !== null && lo !== null) {
        lat = ref(0x0001) === 'S' ? -la : la;
        lon = ref(0x0003) === 'W' ? -lo : lo;
        // Le point nul au large du golfe de Guinee est le symptome d'un EXIF
        // vide, jamais d'une intervention.
        if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180
            || (Math.abs(lat) < 0.0001 && Math.abs(lon) < 0.0001)) { lat = null; lon = null; }
      }
    }

    return { date, lat, lon };
  } catch { return RIEN; }
}

/**
 * L'adresse d'un point, retenue une fois pour toutes.
 *
 * Un compte rendu porte trente photos du meme chantier : sans ce cache, c'est
 * trente geocodages inverses pour la meme facade. La cle arrondit au
 * dix-millieme de degre, une dizaine de metres — deux photos prises du meme
 * trottoir partagent donc leur adresse.
 */
const _adressesVues = new Map();
async function adresseDuPointRetenue(lat, lon) {
  const cle = lat.toFixed(4) + ',' + lon.toFixed(4);
  if (_adressesVues.has(cle)) return _adressesVues.get(cle);
  let libelle = '';
  try {
    const r = await adresseDuPoint(lat, lon);
    libelle = (r && r.libelle) || '';
  } catch { /* hors ligne : les coordonnees feront foi */ }
  _adressesVues.set(cle, libelle);
  return libelle;
}
function horodatage(d) {
  const deux = n => String(n).padStart(2, '0');
  return deux(d.getDate()) + '/' + deux(d.getMonth() + 1) + '/' + d.getFullYear()
    + '  ' + deux(d.getHours()) + ':' + deux(d.getMinutes());
}

/**
 * Dessine le bandeau en bas de l'image.
 *
 * En bas à gauche, sur toute la largeur : c'est la seule place qui ne soit
 * jamais rognée par un aperçu, et le fond sombre garantit la lecture quelle
 * que soit la photo dessous. La taille suit la largeur de l'image, sinon le
 * texte devient illisible sur un grand cliché et déborde sur un petit.
 */
function incruster(ctx, w, h, lignes) {
  const taille = Math.max(11, Math.round(w / 34));
  const interligne = Math.round(taille * 1.35);
  const marge = Math.round(taille * 0.6);
  const hauteur = lignes.length * interligne + marge * 2 - (interligne - taille);

  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.fillRect(0, h - hauteur, w, hauteur);

  ctx.font = '600 ' + taille + 'px -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#ffffff';
  // Une ombre portée : sur un bandeau semi-transparent posé sur un mur blanc,
  // le texte blanc seul se perd.
  ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
  ctx.shadowBlur = Math.round(taille / 4);

  let y = h - hauteur + marge;
  for (const l of lignes) {
    // Une adresse longue est coupée plutôt que débordée.
    let texte = l;
    while (texte && ctx.measureText(texte).width > w - marge * 2) {
      texte = texte.slice(0, -2);
    }
    if (texte !== l && texte.length > 3) texte = texte.slice(0, -1) + '…';
    ctx.fillText(texte, marge, y);
    y += interligne;
  }
  ctx.restore();
}

/**
 * Prépare une photo : redimensionnée, compressée, horodatée et située.
 *
 * Remplace l'ancien `compressImage`, qui faisait les deux premières choses.
 * L'horodatage se pose APRÈS le redimensionnement : incrusté avant, il serait
 * réduit avec l'image et deviendrait illisible.
 */
export function preparerPhoto(dataUrl, { adresseCommande, maxPx = 1200, qualite = 0.75, sansBandeau = false } = {}) {
  return new Promise(resolve => {
    const img = new Image();
    img.onerror = () => resolve(dataUrl);   // mieux vaut la photo brute que rien
    img.onload = async () => {
      let w = img.width, h = img.height;
      if (w > maxPx || h > maxPx) {
        if (w > h) { h = Math.round(h * maxPx / w); w = maxPx; }
        else { w = Math.round(w * maxPx / h); h = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);

      if (sansBandeau) return resolve(canvas.toDataURL('image/jpeg', qualite));

      // Ce que la photo dit d'elle-meme passe avant ce que le telephone dit
      // de l'instant present : une photo prise a 9 h sur le chantier et
      // deposee a 20 h au domicile doit porter le chantier.
      const meta = metaPhoto(dataUrl);

      let ou = null;
      if (meta.lat !== null) {
        const libelle = await Promise.race([
          adresseDuPointRetenue(meta.lat, meta.lon),
          dormir(9000).then(() => ''),
        ]);
        ou = { libelle, source: 'photo', lat: meta.lat, lon: meta.lon };
      } else {
        try {
          ou = await Promise.race([
            lieu(adresseCommande === undefined ? _adresseCommande : adresseCommande),
            dormir(9000).then(() => null),
          ]);
        } catch { /* on horodate quand même */ }
      }

      // L'heure de la photo, pas celle du formulaire. Au-dela de cinq
      // minutes d'ecart on affiche les deux : sinon une photo du matin
      // deposee le soir porterait l'heure du soir sans que rien ne le dise.
      const maintenant = new Date();
      const prise = meta.date;
      const decale = prise && maintenant - prise > 5 * 60 * 1000;
      const lignes = [decale
        ? horodatage(prise) + '   (deposee ' + horodatage(maintenant).slice(-5) + ')'
        : horodatage(maintenant)];
      if (ou && ou.libelle) {
        lignes.push(ou.libelle);
        // Dire d'où vient l'adresse : sans cela, une adresse de commande
        // affichée sur une photo prise ailleurs serait un faux.
        if (ou.source === 'commande') lignes.push('adresse de la commande (position indisponible)');
        // La position vient de la photo elle-meme : c'est la plus solide, et
        // elle reste vraie quelle que soit l'heure du depot.
        else if (ou.source === 'photo') lignes.push('position de la prise de vue — ' + ou.lat.toFixed(5) + ', ' + ou.lon.toFixed(5));
        // La position est celle du depot : si la photo est plus ancienne,
        // le dire plutot que de laisser croire qu'elle a ete relevee la.
        else if (decale && ou.lat) lignes.push('position au depot — ' + ou.lat.toFixed(5) + ', ' + ou.lon.toFixed(5));
        else if (ou.lat) lignes.push(ou.lat.toFixed(5) + ', ' + ou.lon.toFixed(5));
      } else if (ou && ou.lat) {
        lignes.push((ou.source === 'photo' ? 'position de la prise de vue — ' : '')
          + ou.lat.toFixed(5) + ', ' + ou.lon.toFixed(5));
      } else {
        lignes.push('position indisponible');
      }

      incruster(ctx, w, h, lignes);
      resolve(canvas.toDataURL('image/jpeg', qualite));
    };
    img.src = dataUrl;
  });
}
